import { createSlice } from '@reduxjs/toolkit';
import { getRestaurantStorageItem, setRestaurantStorageItem } from '../utils/restaurantStorageUtils';
import {
  applyPerItemCustomPriceIfNeeded,
  dealHasSlotLevelPricing,
  getPerItemCustomPrice,
  getRealDealSlotSelectedItems,
  isFixedPricePerItemType,
  resolveDealSlotPricing,
} from '../utils/dealSlotPricing';
import { normalizeSeatNo } from '../utils/seatUtils';
import { getDealOrComboMergeKey } from '../utils/cartItemMerge';
import { getSimpleCartItemLineTotal } from '../utils/cartItemPricing';

const CART_STORAGE_KEY = 'cart';

/** Empty customer info used when no data is stored and after customer logout */
const EMPTY_CUSTOMER_INFO = {
  fullName: '',
  phoneNo: '',
  phone: '',
  address: '',
  deliveryAddress: '',
  aptNo: '',
  postalCode: '',
  deliveryInstructions: '',
  orderComments: '',
  company: '',
  pickupFrom: '',
  locationId: null,
  serviceAreaId: '',
  serviceAreaName: '',
  serviceArea: '',
  tableId: null,
  tableName: '',
  tableNumber: '',
  pickupDate: '',
  pickupTime: '',
};

/**
 * Get cart state for a specific restaurant
 */
const loadCartState = (restaurantSlug) => {
  if (!restaurantSlug) {
    return {
      items: [],
      orderType: 'pickup',
      customerInfo: { ...EMPTY_CUSTOMER_INFO },
      deliveryFeeQuote: null,
      pendingInvoiceNo: '',
    };
  }
  
  try {
    const stored = getRestaurantStorageItem(restaurantSlug, CART_STORAGE_KEY);
    if (stored) {
      // Normalize items to ensure modifiers is always an array; drop invalid Seat 0
      const normalizedItems = (stored.items || []).map(item => ({
        ...item,
        modifiers: Array.isArray(item.modifiers) ? item.modifiers : [],
        seatNo: normalizeSeatNo(item?.seatNo),
      }));
      
      const mergedCustomer = stored.customerInfo
        ? { ...EMPTY_CUSTOMER_INFO, ...stored.customerInfo }
        : { ...EMPTY_CUSTOMER_INFO };
      mergedCustomer.seatNo = normalizeSeatNo(mergedCustomer.seatNo);

      return {
        items: normalizedItems,
        orderType: stored.orderType || 'pickup',
        customerInfo: mergedCustomer,
        deliveryFeeQuote: stored.deliveryFeeQuote ?? null,
        pendingInvoiceNo: stored.pendingInvoiceNo || '',
      };
    }
  } catch (error) {
    console.error('Error loading cart state:', error);
  }
  
  return {
    items: [],
    orderType: 'pickup',
    customerInfo: { ...EMPTY_CUSTOMER_INFO },
    deliveryFeeQuote: null,
    pendingInvoiceNo: '',
  };
};

/**
 * Save cart state for a specific restaurant
 */
const saveCartState = (restaurantSlug, state) => {
  if (!restaurantSlug) {
    return;
  }
  try {
    setRestaurantStorageItem(restaurantSlug, CART_STORAGE_KEY, {
      items: state.items,
      orderType: state.orderType,
      customerInfo: state.customerInfo,
      deliveryFeeQuote: state.deliveryFeeQuote ?? null,
      pendingInvoiceNo: state.pendingInvoiceNo ?? '',
    });
  } catch (error) {
    console.error('Error saving cart state:', error);
  }
};

const initialState = {
  // Store carts per restaurant: { [restaurantSlug]: cartState }
  carts: {},
  // Current restaurant slug (will be set by middleware or actions)
  currentRestaurantSlug: null,
};

const sanitizeCustomerInfoByOrderType = (customerInfo = {}, orderType = 'pickup') => {
  const normalizedOrderType = orderType === 'collection' ? 'pickup' : orderType;
  const nextCustomerInfo = { ...customerInfo };

  if (normalizedOrderType === 'delivery') {
    nextCustomerInfo.pickupDate = '';
    nextCustomerInfo.pickupTime = '';
    nextCustomerInfo.serviceAreaId = '';
    nextCustomerInfo.serviceAreaName = '';
    nextCustomerInfo.serviceArea = '';
    nextCustomerInfo.tableId = null;
    nextCustomerInfo.tableName = '';
    nextCustomerInfo.tableNumber = '';
    return nextCustomerInfo;
  }

  if (normalizedOrderType === 'pickup') {
    nextCustomerInfo.address = '';
    nextCustomerInfo.deliveryAddress = '';
    nextCustomerInfo.aptNo = '';
    // Keep postalCode so switching back to delivery can re-quote the same postcode
    nextCustomerInfo.deliveryInstructions = '';
    nextCustomerInfo.orderComments = '';
    nextCustomerInfo.serviceAreaId = '';
    nextCustomerInfo.serviceAreaName = '';
    nextCustomerInfo.serviceArea = '';
    nextCustomerInfo.tableId = null;
    nextCustomerInfo.tableName = '';
    nextCustomerInfo.tableNumber = '';
    return nextCustomerInfo;
  }

  // dine-in and browse share table fields; clear delivery/pickup-only fields
  const dineInLike =
    normalizedOrderType === 'dine-in' ||
    normalizedOrderType === 'dineIn' ||
    normalizedOrderType === 'Dine-In' ||
    normalizedOrderType === 'browse';

  if (dineInLike) {
    nextCustomerInfo.address = '';
    nextCustomerInfo.deliveryAddress = '';
    nextCustomerInfo.aptNo = '';
    nextCustomerInfo.postalCode = '';
    nextCustomerInfo.deliveryInstructions = '';
    nextCustomerInfo.orderComments = '';
    nextCustomerInfo.pickupDate = '';
    nextCustomerInfo.pickupTime = '';
    return nextCustomerInfo;
  }

  return nextCustomerInfo;
};

/**
 * Mirrors the API's GetOrderTypePrice. A delivery or takeaway price only counts when it is set to
 * a positive amount; otherwise the sale price applies. Treating a stored 0 as a real price put the
 * cart and the server on different totals, which surfaced as the order being rejected at submit.
 */
const resolveOrderTypePrice = (source, orderType, fallbackPrice = 0) => {
  const firstPositive = (...candidates) => {
    for (const candidate of candidates) {
      const value = parseFloat(candidate);
      if (Number.isFinite(value) && value > 0) return value;
    }
    return null;
  };

  if (!source || typeof source !== 'object') {
    return firstPositive(fallbackPrice) ?? 0;
  }

  const salePrice = firstPositive(
    source.MenuItem_SalePrice,
    source.itemSalePrice,
    source.salePrice,
    source.price,
    fallbackPrice
  );

  const key = String(orderType || '').toLowerCase().replace(/[\s_-]/g, '');
  if (key === 'delivery') {
    return firstPositive(source.MenuItem_DeliveryPrice, source.deliveryPrice) ?? salePrice ?? 0;
  }
  if (key === 'collection' || key === 'pickup' || key === 'takeaway') {
    return firstPositive(source.MenuItem_TakeAwayPrice, source.takeAwayPrice) ?? salePrice ?? 0;
  }
  return salePrice ?? 0;
};

const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    // Initialize cart for a restaurant
    initializeCart: (state, action) => {
      const { restaurantSlug } = action.payload;
      if (restaurantSlug && !state.carts[restaurantSlug]) {
        state.carts[restaurantSlug] = loadCartState(restaurantSlug);
        state.currentRestaurantSlug = restaurantSlug;
      }
    },
    
    // Set current restaurant
    setCurrentRestaurant: (state, action) => {
      const { restaurantSlug } = action.payload;
      state.currentRestaurantSlug = restaurantSlug;
      if (restaurantSlug && !state.carts[restaurantSlug]) {
        state.carts[restaurantSlug] = loadCartState(restaurantSlug);
      }
    },
    
    // Sync cart from localStorage (used for cross-tab synchronization)
    syncCartFromStorage: (state, action) => {
      const { restaurantSlug } = action.payload;
      if (!restaurantSlug) {
        return;
      }
      
      // Load the latest cart state from localStorage
      const syncedCart = loadCartState(restaurantSlug);
      
      // Update Redux state with the synced cart (don't save back to avoid loops)
      state.carts[restaurantSlug] = syncedCart;
    },
    
    // Add item to cart
    addItem: (state, action) => {
      const { restaurantSlug, item } = action.payload;
      if (!restaurantSlug || !state.carts[restaurantSlug]) {
        return;
      }
      
      const cart = state.carts[restaurantSlug];
      // Ensure modifiers is always an array
      const modifiers = Array.isArray(item.modifiers) ? item.modifiers : [];
      const addQty = Math.max(1, parseFloat(item.quantity) || 1);

      // Identical deals/combos merge into one line with increased quantity
      const mergeKey = getDealOrComboMergeKey({ ...item, modifiers, seatNo: normalizeSeatNo(item?.seatNo) });
      if (mergeKey) {
        const existing = cart.items.find((existingItem) => getDealOrComboMergeKey(existingItem) === mergeKey);
        if (existing) {
          existing.quantity = (parseFloat(existing.quantity) || 1) + addQty;
          saveCartState(restaurantSlug, cart);
          return;
        }
      }

      // Always generate a unique cart item ID to prevent deletion issues with duplicate items
      // Store the original product ID as productId for reference if needed
      const uniqueCartItemId = `cart_item_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      cart.items.push({
        ...item,
        id: uniqueCartItemId,
        productId: item.id, // Store original product ID separately for reference
        modifiers: modifiers,
        quantity: addQty,
        seatNo: normalizeSeatNo(item?.seatNo),
      });
      saveCartState(restaurantSlug, cart);
    },
    
    // Remove item from cart
    removeItem: (state, action) => {
      const { restaurantSlug, itemId } = action.payload;
      if (!restaurantSlug || !state.carts[restaurantSlug]) {
        return;
      }
      
      const cart = state.carts[restaurantSlug];
      cart.items = cart.items.filter(item => item.id !== itemId);
      saveCartState(restaurantSlug, cart);
    },
    
    // Update item quantity
    updateItemQuantity: (state, action) => {
      const { restaurantSlug, itemId, quantity } = action.payload;
      if (!restaurantSlug || !state.carts[restaurantSlug]) {
        return;
      }
      
      const cart = state.carts[restaurantSlug];
      const item = cart.items.find(item => item.id === itemId);
      if (item) {
        if (quantity <= 0) {
          cart.items = cart.items.filter(i => i.id !== itemId);
        } else {
          // Modifier quantities stay per-unit; line total multiplies by item.quantity.
          // Do not scale modifier.quantity here — that double-counted toppings with the line formula.
          item.quantity = quantity;
        }
        saveCartState(restaurantSlug, cart);
      }
    },
    
    // Update item modifiers
    updateItemModifiers: (state, action) => {
      const { restaurantSlug, itemId, modifiers } = action.payload;
      if (!restaurantSlug || !state.carts[restaurantSlug]) {
        return;
      }
      
      const cart = state.carts[restaurantSlug];
      const item = cart.items.find(item => item.id === itemId);
      if (item) {
        // Ensure modifiers is always an array
        item.modifiers = Array.isArray(modifiers) ? modifiers : [];
        saveCartState(restaurantSlug, cart);
      }
    },
    
    // Update entire item (for editing cart items)
    updateItem: (state, action) => {
      const { restaurantSlug, itemId, updatedItem } = action.payload;
      if (!restaurantSlug || !state.carts[restaurantSlug]) {
        return;
      }
      
      const cart = state.carts[restaurantSlug];
      const itemIndex = cart.items.findIndex(item => item.id === itemId);
      if (itemIndex !== -1) {
        // Ensure modifiers is always an array
        const modifiers = Array.isArray(updatedItem.modifiers) ? updatedItem.modifiers : [];
        cart.items[itemIndex] = {
          ...cart.items[itemIndex],
          ...updatedItem,
          id: itemId, // Preserve the original ID
          modifiers: modifiers,
          seatNo: normalizeSeatNo(
            updatedItem?.seatNo !== undefined
              ? updatedItem.seatNo
              : cart.items[itemIndex]?.seatNo
          ),
        };
        saveCartState(restaurantSlug, cart);
      }
    },
    
    setDeliveryFeeQuote: (state, action) => {
      const { restaurantSlug, quote } = action.payload;
      if (!restaurantSlug || !state.carts[restaurantSlug]) {
        return;
      }
      const next = quote ?? null;
      const prev = state.carts[restaurantSlug].deliveryFeeQuote ?? null;
      // Skip no-op writes so quote effects / selectors do not thrash.
      if (
        prev === next ||
        (prev &&
          next &&
          prev.fee === next.fee &&
          prev.matched === next.matched &&
          prev.ruleID === next.ruleID &&
          prev.pattern === next.pattern &&
          prev.source === next.source)
      ) {
        return;
      }
      state.carts[restaurantSlug].deliveryFeeQuote = next;
      saveCartState(restaurantSlug, state.carts[restaurantSlug]);
    },

    // Set order type and update prices for all cart items
    setOrderType: (state, action) => {
      const { restaurantSlug, orderType } = action.payload;
      if (!restaurantSlug || !state.carts[restaurantSlug]) {
        return;
      }
      
      const cart = state.carts[restaurantSlug];
      const previousOrderType = cart.orderType;
      cart.orderType = orderType;
      cart.customerInfo = sanitizeCustomerInfoByOrderType(cart.customerInfo, orderType);
      
      // If order type changed, recalculate prices for all items
      // BUT skip deal items - their price is already correctly calculated with discounts when added to cart
      if (previousOrderType !== orderType && cart.items.length > 0) {
        console.log(`Order type changed from ${previousOrderType} to ${orderType}, recalculating prices for ${cart.items.length} items`);
        cart.items.forEach((item, index) => {
          // Skip deal items - their price already includes discounted items and modifiers
          // Recalculating would lose the discount information
          if (item.isDeal && item.dealSlots && item.dealSlots.length > 0) {
            console.log(`Skipping price recalculation for deal item: ${item.name} (price already includes discounts)`);
            return;
          }
          
          const oldPrice = item.price;
          let newPrice = parseFloat(item.price) || 0;
          let priceUpdated = false;
          
          // Check if item has a variant (variant name is in the item name like "Item Name (Variant Name)")
          const variantMatch = item.name && item.name.match(/\(([^)]+)\)$/);
          const variantName = variantMatch ? variantMatch[1] : null;
          
          // First, try to get price from originalItem
          if (item.originalItem) {
            const originalItem = item.originalItem;
            
            // If item has a variant, try to find variant price from variants array
            if (variantName && item.variants && Array.isArray(item.variants) && item.variants.length > 0) {
              const selectedVariant = item.variants.find(v => 
                (v.variantName === variantName) || 
                (v.name === variantName) ||
                (v.itemName && v.itemName.includes(variantName))
              );
              
              if (selectedVariant) {
                newPrice = resolveOrderTypePrice(selectedVariant, orderType, item.price);
                priceUpdated = true;
              }
            }
            
            // If variant price not found or no variant, use base item price
            if (!priceUpdated) {
              newPrice = resolveOrderTypePrice(originalItem, orderType, item.price);
              priceUpdated = true;
            }
          } else {
            // Fallback: the item carries its own order-type prices when originalItem is missing.
            newPrice = resolveOrderTypePrice(item, orderType, item.price);
            priceUpdated = true;
          }
          
          // Update the price if we found a new one
          if (priceUpdated) {
            item.price = typeof newPrice === 'number' ? newPrice.toFixed(2) : String(newPrice);
            console.log(`Item ${index + 1} (${item.name}): Price updated from ${oldPrice} to ${item.price}`);
          } else {
            console.warn(`Item ${index + 1} (${item.name}): Could not update price. originalItem:`, !!item.originalItem, 'has deliveryPrice:', !!item.deliveryPrice, 'has takeAwayPrice:', !!item.takeAwayPrice);
          }
          
          // Recalculate modifier prices if they have order-type-specific pricing
          if (item.modifiers && Array.isArray(item.modifiers) && item.modifiers.length > 0) {
            item.modifiers = item.modifiers.map((modifier) => {
              // Prefer prices stored on the modifier, then the catalog copy it was built from, then
              // the matching option on the parent item. All three go through the same order-type
              // rule the API applies when it reprices the line.
              const catalogSources = [modifier, modifier.originalModifier];

              if (item.originalItem?.modifierGroups) {
                for (const group of item.originalItem.modifierGroups) {
                  const match = (group.modifiers || []).find(
                    (m) => m.modifierName === modifier.name || m.name === modifier.name
                  );
                  if (match) {
                    catalogSources.push(match);
                    break;
                  }
                }
              }

              const source = catalogSources.find(
                (candidate) =>
                  candidate &&
                  (candidate.MenuItem_DeliveryPrice !== undefined ||
                    candidate.deliveryPrice !== undefined ||
                    candidate.MenuItem_TakeAwayPrice !== undefined ||
                    candidate.takeAwayPrice !== undefined ||
                    candidate.MenuItem_SalePrice !== undefined)
              );

              if (!source) {
                // Nothing order-type aware to go on — leave the stored add-on price alone.
                return modifier;
              }

              const newModifierPrice = resolveOrderTypePrice(source, orderType, modifier.price);
              const wasCharged =
                modifier.chargePrice === undefined || modifier.chargePrice === null
                  ? true
                  : parseFloat(modifier.chargePrice) > 0;
              return {
                ...modifier,
                price: newModifierPrice.toFixed(2),
                // Keep freeSelection outcome: free stays 0, paid follows order-type catalog price
                chargePrice: wasCharged ? newModifierPrice : 0,
              };
            });
          }
        });
      }
      
      saveCartState(restaurantSlug, cart);
    },
    
    // Update customer info
    updateCustomerInfo: (state, action) => {
      const { restaurantSlug, customerInfo } = action.payload;
      if (!restaurantSlug || !state.carts[restaurantSlug]) {
        return;
      }
      
      const cart = state.carts[restaurantSlug];
      const nextInfo = { ...cart.customerInfo, ...customerInfo };
      if (Object.prototype.hasOwnProperty.call(customerInfo || {}, "seatNo")) {
        nextInfo.seatNo = normalizeSeatNo(customerInfo.seatNo);
      } else {
        nextInfo.seatNo = normalizeSeatNo(nextInfo.seatNo);
      }
      cart.customerInfo = nextInfo;
      saveCartState(restaurantSlug, cart);
    },
    
    setPendingInvoiceNo: (state, action) => {
      const { restaurantSlug, invoiceNo } = action.payload;
      if (!restaurantSlug) return;
      if (!state.carts[restaurantSlug]) {
        state.carts[restaurantSlug] = loadCartState(restaurantSlug);
      }
      const cart = state.carts[restaurantSlug];
      cart.pendingInvoiceNo = String(invoiceNo || '').trim();
      saveCartState(restaurantSlug, cart);
    },

    clearPendingInvoiceNo: (state, action) => {
      const { restaurantSlug } = action.payload;
      if (!restaurantSlug || !state.carts[restaurantSlug]) return;
      const cart = state.carts[restaurantSlug];
      cart.pendingInvoiceNo = '';
      saveCartState(restaurantSlug, cart);
    },

    // Clear cart
    clearCart: (state, action) => {
      const { restaurantSlug } = action.payload;
      if (!restaurantSlug || !state.carts[restaurantSlug]) {
        return;
      }

      // Clear items only. Keep orderType + customerInfo (especially locationId/pickupFrom)
      // so the UI remains scoped to the selected company/branch after submitting an order.
      const cart = state.carts[restaurantSlug];
      cart.items = [];
      cart.pendingInvoiceNo = '';
      saveCartState(restaurantSlug, cart);
    },

    // Clear order-session specific details after successful order submission.
    // Keeps identity/location context while forcing next order to reselect time/table/instructions.
    // For dine-in (especially QR table links), keep service area + table locked so guests
    // can place another order from the same table without losing context in the basket.
    clearOrderSessionDetails: (state, action) => {
      const { restaurantSlug } = action.payload;
      if (!restaurantSlug || !state.carts[restaurantSlug]) {
        return;
      }

      const cart = state.carts[restaurantSlug];
      const orderTypeKey = String(cart.orderType || "")
        .toLowerCase()
        .replace(/[\s_-]+/g, "");
      const isDineInSession =
        orderTypeKey === "dinein" || orderTypeKey === "dine" || orderTypeKey === "eatin";

      const preservedDineIn = isDineInSession
        ? {
            serviceAreaId: cart.customerInfo?.serviceAreaId ?? "",
            serviceAreaName: cart.customerInfo?.serviceAreaName ?? "",
            serviceArea: cart.customerInfo?.serviceArea ?? "",
            tableId: cart.customerInfo?.tableId ?? null,
            tableName: cart.customerInfo?.tableName ?? "",
            tableNumber: cart.customerInfo?.tableNumber ?? "",
            numberOfSeats: cart.customerInfo?.numberOfSeats,
            seatNo: normalizeSeatNo(cart.customerInfo?.seatNo),
            locationId: cart.customerInfo?.locationId ?? null,
          }
        : {
            serviceAreaId: "",
            serviceAreaName: "",
            serviceArea: "",
            tableId: null,
            tableName: "",
            tableNumber: "",
          };

      cart.customerInfo = {
        ...cart.customerInfo,
        // Shared notes/instructions
        deliveryInstructions: "",
        orderComments: "",
        // Delivery details
        address: "",
        deliveryAddress: "",
        aptNo: "",
        postalCode: isDineInSession ? "" : cart.customerInfo?.postalCode ?? "",
        // Collection details
        pickupDate: "",
        pickupTime: "",
        // Dine-in: preserve QR / locked table context
        ...preservedDineIn,
      };
      saveCartState(restaurantSlug, cart);
    },

    // Clear customer info for all restaurants (e.g. on customer logout so next user does not see previous user's data)
    clearAllCustomerInfoOnLogout: (state) => {
      Object.keys(state.carts).forEach((slug) => {
        const cart = state.carts[slug];
        if (cart) {
          cart.customerInfo = { ...EMPTY_CUSTOMER_INFO };
          saveCartState(slug, cart);
        }
      });
    },
  },
});

export const {
  initializeCart,
  setCurrentRestaurant,
  syncCartFromStorage,
  addItem,
  removeItem,
  updateItemQuantity,
  updateItemModifiers,
  updateItem,
  setOrderType,
  setDeliveryFeeQuote,
  updateCustomerInfo,
  clearCart,
  setPendingInvoiceNo,
  clearPendingInvoiceNo,
  clearOrderSessionDetails,
  clearAllCustomerInfoOnLogout,
} = cartSlice.actions;

// Selectors
export const selectCart = (state, restaurantSlug) => {
  if (!restaurantSlug) {
    return {
      items: [],
      orderType: 'pickup',
      customerInfo: { ...EMPTY_CUSTOMER_INFO },
      deliveryFeeQuote: null,
      pendingInvoiceNo: '',
    };
  }
  return state.cart.carts[restaurantSlug] || {
    items: [],
    orderType: 'pickup',
    customerInfo: { ...EMPTY_CUSTOMER_INFO },
    deliveryFeeQuote: null,
  };
};

export const selectCartItems = (state, restaurantSlug) => {
  const cart = selectCart(state, restaurantSlug);
  return cart.items;
};

export const selectCartTotal = (state, restaurantSlug) => {
  const cart = selectCart(state, restaurantSlug);
  return cart.items.reduce((total, item) => {
    const itemQuantity = parseFloat(item.quantity || 1);
    
    // For deal items with dealSlots, always recalculate total with discounted prices
    // This ensures we always use discounted prices, not original prices
      if (item.isDeal && item.dealSlots && item.dealSlots.length > 0) {
      // Get deal info to apply discounts
      const dealInfo = item.originalItem || {};
      const dealSlotsForPricing = item.dealSlots.map((slot) => ({
        ...slot,
        selectedItems: getRealDealSlotSelectedItems(slot, dealInfo),
      }));
      const dealPricingType = (dealInfo.dealPricingType || "").trim().toLowerCase();
      const dealDiscountPercentage = dealInfo.dealDiscountPercentage || 0;
      const buyQuantity = dealInfo.buyQuantity ?? dealInfo.BuyQuantity ?? 1;
      const dealPrice = parseFloat(dealInfo.dealPrice || 0);
      const minimumOrderValue = dealInfo.minimumOrderValue || dealInfo.MinimumOrderValue || 0;
      
      let dealTotal = 0;
      
      // Check if it's a fixed price deal
      if (dealPrice > 0) {
        // Fixed deal price - only add discounted modifier prices (items are included in dealPrice)
        dealTotal = dealPrice;
        
        // Collect all items with their modifier prices
        const allItems = [];
        dealSlotsForPricing.forEach((slot) => {
          if (slot.selectedItems && Array.isArray(slot.selectedItems)) {
            slot.selectedItems.forEach((selectedItem) => {
              let modifierPrice = 0;
              
              // Calculate modifier price from modifiers array (original prices)
              if (selectedItem.modifiers && Array.isArray(selectedItem.modifiers)) {
                selectedItem.modifiers.forEach((mod) => {
                  const modPrice = parseFloat(mod.price || 0);
                  const modQuantity = parseFloat(mod.quantity || 1);
                  modifierPrice += modPrice * modQuantity;
                });
              }
              
              allItems.push({
                modifierPrice: modifierPrice
              });
            });
          }
        });
        
        // Modifiers ALWAYS stay at full price (deal discounts apply to items only).
        allItems.forEach((itemData) => {
          dealTotal += itemData.modifierPrice;
        });
      } else {
        // Dynamic pricing - check if we should use slot-level pricing or deal-level BOGO logic
        // Use slot-level pricing when: minimumOrderValue is set, OR any slot has explicit slot-level pricing
        // This matches DealSummary.jsx and SideCart logic exactly
        const useSlotLevelPricing = (minimumOrderValue > 0) || dealHasSlotLevelPricing(dealInfo, dealSlotsForPricing);
        
        if (useSlotLevelPricing) {
          // Use slot-level pricing when minimumOrderValue is set OR slot has explicit pricing
          // This matches the exact approach used in DealSummary.jsx and SideCart
          dealSlotsForPricing.forEach((slot) => {
            if (slot.selectedItems && Array.isArray(slot.selectedItems)) {
              const resolvedSlot = resolveDealSlotPricing(slot, dealInfo);
              const slotPricingType = resolvedSlot.pricingType;
              const isSlotFree = resolvedSlot.isFree;
              const slotDiscountPercentage = resolvedSlot.discountPercentage;
              const slotCustomPrice = resolvedSlot.customPrice;
              const slotBuyQuantity = slot?.buyQuantity ?? slot?.BuyQuantity ?? null;
              const buyQuantity = slotBuyQuantity !== null ? slotBuyQuantity : (dealInfo.buyQuantity ?? dealInfo.BuyQuantity ?? 1);
              const slotAutoDup = (slot?.autoDuplicateItem ?? slot?.AutoDuplicateItem ?? dealInfo?.autoDuplicateItem ?? dealInfo?.AutoDuplicateItem) === true;
              
              // Fixed Price (Per Slot): one price for the whole slot, then modifier prices per item (match DealSummary)
              if (slotPricingType === "FixedPricePerSlot" && slotCustomPrice != null && !isNaN(parseFloat(slotCustomPrice))) {
                dealTotal += parseFloat(slotCustomPrice);
                slot.selectedItems.forEach((selectedItem) => {
                  const itemId = selectedItem.itemId || selectedItem.item?.itemId || selectedItem.id;
                  const lookupKey = selectedItem.isAutoDuplicated ? `${itemId}_duplicate` : itemId;
                  let modifierPrice = 0;
                  if (slot.itemModifiers?.[lookupKey]) modifierPrice = parseFloat(slot.itemModifiers[lookupKey].modifierPrice || 0);
                  else if (slot.itemModifiers?.[itemId]) modifierPrice = parseFloat(slot.itemModifiers[itemId].modifierPrice || 0);
                  if (modifierPrice === 0 && selectedItem.modifiers && Array.isArray(selectedItem.modifiers)) {
                    selectedItem.modifiers.forEach((mod) => {
                      modifierPrice += parseFloat(mod.price || 0) * parseFloat(mod.quantity || 1);
                    });
                  }
                  dealTotal += modifierPrice;
                });
                return;
              }
              
              // If auto-duplicate BOGO and buyQuantity is 0, treat as buy-1-get-1-free.
              const effectiveBuyQuantity = (slotAutoDup && buyQuantity === 0) ? 1 : buyQuantity;
              
              // For Free pricing with BOGO (buyQuantity > 0), collect items for sorting
              const needsBOGOSorting = (slotPricingType === "Free" || isSlotFree) && effectiveBuyQuantity > 0 && !slotAutoDup;
              // For HalfPrice / Discount slots with buyQuantity > 0, also need BOGO sorting:
              // only the cheapest (total - buyQty) items get discounted.
              const needsHalfDiscountBOGOSorting =
                (slotPricingType === "HalfPrice" ||
                  ((slotPricingType === "Discount" || slotPricingType === "CustomDiscount") && slotDiscountPercentage)) &&
                effectiveBuyQuantity > 0 &&
                !slotAutoDup;
              let slotItems = [];
              
              // Collect items for BOGO sorting if needed (only for standard BOGO, not auto-duplicate)
              if (needsBOGOSorting || needsHalfDiscountBOGOSorting) {
                slot.selectedItems.forEach((selectedItem, idx) => {
                  const itemId = selectedItem.itemId || selectedItem.item?.itemId || selectedItem.id;
                  const isAutoDuplicated = selectedItem.isAutoDuplicated === true;
                  const modifierLookupKey = isAutoDuplicated ? `${itemId}_duplicate` : itemId;
                  
                  // Get item price
                  let itemPrice = parseFloat(selectedItem.itemPrice || selectedItem.item?.price || selectedItem.price || 0);
                  
                  // Get modifier price
                  let modifierPrice = 0;
                  if (slot.itemModifiers && slot.itemModifiers[modifierLookupKey]) {
                    modifierPrice = parseFloat(slot.itemModifiers[modifierLookupKey].modifierPrice || 0);
                  } else if (slot.itemModifiers && slot.itemModifiers[itemId]) {
                    modifierPrice = parseFloat(slot.itemModifiers[itemId].modifierPrice || 0);
                  }
                  if (modifierPrice === 0 && selectedItem.modifiers && Array.isArray(selectedItem.modifiers)) {
                    selectedItem.modifiers.forEach((mod) => {
                      const modPrice = parseFloat(mod.price || 0);
                      const modQuantity = parseFloat(mod.quantity || 1);
                      modifierPrice += modPrice * modQuantity;
                    });
                  }
                  
                  const totalPrice = itemPrice + modifierPrice;
                  
                  slotItems.push({
                    itemIndex: idx,
                    itemPrice: itemPrice,
                    modifierPrice: modifierPrice,
                    totalPrice: totalPrice,
                    selectedItem: selectedItem,
                    itemId: itemId,
                    isAutoDuplicated: isAutoDuplicated
                  });
                });
                
                // Sort by total price (cheapest first)
                slotItems.sort((a, b) => a.totalPrice - b.totalPrice);
              }
              
              slot.selectedItems.forEach((selectedItem, itemIdx) => {
                // Use EXACT same item price extraction order as DealSummary.jsx and SideCart
                let itemPrice = parseFloat(selectedItem.itemPrice || selectedItem.item?.price || selectedItem.price || 0);
                const itemId = selectedItem.itemId || selectedItem.item?.itemId || selectedItem.id;
                const isAutoDuplicated = selectedItem.isAutoDuplicated === true || selectedItem.isAutoDuplicated === "true" || selectedItem.isAutoDuplicated === 1;
                const modifierLookupKey = isAutoDuplicated ? `${itemId}_duplicate` : itemId;
                
                // Get modifier price - use same approach as DealSummary.jsx and SideCart
                // DealSummary uses: selection.itemModifiers?.[itemId]?.modifierPrice || 0
                let modifierPrice = 0;
                if (slot.itemModifiers && slot.itemModifiers[modifierLookupKey]) {
                  modifierPrice = parseFloat(slot.itemModifiers[modifierLookupKey].modifierPrice || 0);
                } else if (slot.itemModifiers && slot.itemModifiers[itemId]) {
                  modifierPrice = parseFloat(slot.itemModifiers[itemId].modifierPrice || 0);
                }
                
                // Fallback: Calculate modifier price from modifiers array if not found in itemModifiers
                if (modifierPrice === 0 && selectedItem.modifiers && Array.isArray(selectedItem.modifiers)) {
                  selectedItem.modifiers.forEach((mod) => {
                    const modPrice = parseFloat(mod.price || 0);
                    const modQuantity = parseFloat(mod.quantity || 1);
                    modifierPrice += modPrice * modQuantity;
                  });
                }
                
                let finalItemPrice = itemPrice;
                let finalModifierPrice = modifierPrice;
                
                // Apply slot-level pricing - EXACT same logic as DealSummary.jsx and SideCart
                // IMPORTANT: Handle BOGO logic correctly (buyQuantity > 0 means BOGO, not flat free)
                if (slotPricingType === "Free" || isSlotFree) {
                  // For Free pricing, check buyQuantity to determine if it's flat free (buyQuantity=0) or BOGO (buyQuantity>0)
                  if (effectiveBuyQuantity === 0) {
                    // All items are free (flat free deal)
                    finalItemPrice = 0;
                  } else if (slotAutoDup && effectiveBuyQuantity > 0) {
                    // Auto-duplicate mode: duplicate is always free, original is always paid
                    if (isAutoDuplicated) {
                      finalItemPrice = 0; // Duplicate is always free
                    } else {
                      // Original is always paid - use itemPrice
                      finalItemPrice = itemPrice;
                    }
                  } else if (needsBOGOSorting && slotItems.length > 0) {
                    // Standard BOGO: find item in sorted list and determine if it's free
                    const sortedIndex = slotItems.findIndex(item => {
                      return (item.itemId === itemId || item.selectedItem.itemId === itemId || item.selectedItem.item?.itemId === itemId) &&
                             item.isAutoDuplicated === isAutoDuplicated;
                    });
                    
                    const freeItemCount = Math.max(0, slotItems.length - effectiveBuyQuantity);
                    if (sortedIndex >= 0 && sortedIndex < freeItemCount) {
                      finalItemPrice = 0; // Free (cheapest total price items)
                    } else {
                      finalItemPrice = itemPrice; // Paid (more expensive items)
                    }
                  } else {
                    // Fallback: all items free
                    finalItemPrice = 0;
                  }
                  // Modifiers are NOT free - keep full price
                  finalModifierPrice = modifierPrice;
                } else if (isFixedPricePerItemType(slotPricingType, slotCustomPrice)) {
                  finalItemPrice = getPerItemCustomPrice(slotPricingType, slotCustomPrice);
                  finalModifierPrice = modifierPrice;
                } else if (slotPricingType === "HalfPrice") {
                  // Honor buyQuantity: only cheapest (total - buyQty) items are halved; rest stay full.
                  // Modifiers ALWAYS stay at full price (deal discounts apply to items only).
                  let isDiscounted = true;
                  if (needsHalfDiscountBOGOSorting && slotItems.length > 0) {
                    const sortedIndex = slotItems.findIndex((s) => {
                      return (
                        (s.itemId === itemId ||
                          s.selectedItem.itemId === itemId ||
                          s.selectedItem.item?.itemId === itemId) &&
                        s.isAutoDuplicated === isAutoDuplicated &&
                        s.itemIndex === itemIdx
                      );
                    });
                    const discountedCount = Math.max(0, slotItems.length - effectiveBuyQuantity);
                    isDiscounted = sortedIndex >= 0 && sortedIndex < discountedCount;
                  }
                  finalItemPrice = isDiscounted ? itemPrice * 0.5 : itemPrice;
                  finalModifierPrice = modifierPrice;
                } else if ((slotPricingType === "Discount" || slotPricingType === "CustomDiscount") && slotDiscountPercentage) {
                  // Honor buyQuantity: only cheapest (total - buyQty) items receive the discount.
                  // Modifiers ALWAYS stay at full price (deal discounts apply to items only).
                  let isDiscounted = true;
                  if (needsHalfDiscountBOGOSorting && slotItems.length > 0) {
                    const sortedIndex = slotItems.findIndex((s) => {
                      return (
                        (s.itemId === itemId ||
                          s.selectedItem.itemId === itemId ||
                          s.selectedItem.item?.itemId === itemId) &&
                        s.isAutoDuplicated === isAutoDuplicated &&
                        s.itemIndex === itemIdx
                      );
                    });
                    const discountedCount = Math.max(0, slotItems.length - effectiveBuyQuantity);
                    isDiscounted = sortedIndex >= 0 && sortedIndex < discountedCount;
                  }
                  if (isDiscounted) {
                    const discount = (itemPrice * slotDiscountPercentage) / 100;
                    finalItemPrice = itemPrice - discount;
                  } else {
                    finalItemPrice = itemPrice;
                  }
                  finalModifierPrice = modifierPrice;
                }

                finalItemPrice = applyPerItemCustomPriceIfNeeded(
                  finalItemPrice,
                  itemPrice,
                  slotPricingType,
                  slotCustomPrice
                );

                dealTotal += finalItemPrice + finalModifierPrice;
              });
            }
          });
        } else {
          // Use deal-level BOGO logic when minimumOrderValue is not set
          // Collect all items with their prices for sorting (for BOGO deals)
          const allItems = [];
          dealSlotsForPricing.forEach((slot) => {
            if (slot.selectedItems && Array.isArray(slot.selectedItems)) {
              slot.selectedItems.forEach((selectedItem) => {
                const itemPrice = parseFloat(selectedItem.item?.price || selectedItem.itemPrice || selectedItem.price || 0);
                let modifierPrice = 0;
                
                // Calculate modifier price from modifiers array (original prices)
                if (selectedItem.modifiers && Array.isArray(selectedItem.modifiers)) {
                  selectedItem.modifiers.forEach((mod) => {
                    const modPrice = parseFloat(mod.price || 0);
                    const modQuantity = parseFloat(mod.quantity || 1);
                    modifierPrice += modPrice * modQuantity;
                  });
                }
                
                const totalItemPrice = itemPrice + modifierPrice;
                allItems.push({
                  itemPrice: itemPrice,
                  modifierPrice: modifierPrice,
                  totalPrice: totalItemPrice
                });
              });
            }
          });
          
          // Sort by total price for BOGO deals
          if (dealPricingType === "free" || dealPricingType === "halfprice") {
            allItems.sort((a, b) => a.totalPrice - b.totalPrice);
          }
          
          // Apply discounts
          const totalItems = allItems.length;
          const discountedItemCount = (dealPricingType === "free" || dealPricingType === "halfprice") && buyQuantity > 0
            ? Math.max(0, totalItems - buyQuantity)
            : totalItems;
          
          allItems.forEach((itemData, sortedIndex) => {
            let finalItemPrice = itemData.itemPrice;
            // Modifiers ALWAYS stay at full price; deal discounts apply to items only.
            let finalModifierPrice = itemData.modifierPrice;
            
            if (dealPricingType === "free" && sortedIndex < discountedItemCount) {
              finalItemPrice = 0;
            } else if (dealPricingType === "halfprice" && sortedIndex < discountedItemCount) {
              finalItemPrice = itemData.itemPrice * 0.5;
            } else if ((dealPricingType === "customdiscount" || dealPricingType === "discount") && dealDiscountPercentage) {
              // Apply discount to items only; modifiers stay at full price.
              const itemDiscount = (itemData.itemPrice * dealDiscountPercentage) / 100;
              finalItemPrice = itemData.itemPrice - itemDiscount;
            }
            
            dealTotal += finalItemPrice + finalModifierPrice;
          });
        }
      }
      
      // If dealTotal is still 0 but item has a price, use the stored price
      // This handles cases where calculation might fail or data is incomplete
      if (dealTotal === 0 && item.price && parseFloat(item.price) > 0) {
        return total + (parseFloat(item.price) * itemQuantity);
      }
      
      return total + (dealTotal * itemQuantity);
    }
    
    // Simple items: (unit base + unit modifiers) × item quantity
    return total + getSimpleCartItemLineTotal(item);
  }, 0);
};

export default cartSlice.reducer;

